// @ts-check
/**
 * **実写フィールドの座標系**（Issue #522）。合成フィールド動画のピクセルを平面の座標に写す。
 *
 * ゾーンの位置は `overlay_zones`（フィールド生成の config）で**動画のピクセル**で入っている（`resolveLiveZones`）。
 * 平面へは**中心を合わせて置くだけ**（`scale` 倍するほかは何もしない）。
 *
 * ## 回さない（ユーザー判断 2026-09-24）
 *
 * AviUtl / Premiere の `元動画` ネストは合成フィールド動画を 90° 倒して 1260×1080 の枠に入れる
 * （完成動画は 2 枚のマットが左右に並ぶ）。**ここではその回転をしない** ——
 * 平面はマットが積まれたそのままの向きで、**P1 が手前（v が大きい側）・P2 が奥**になる。
 * 仮想フィールドの理想のグリッドも P1 が手前なので、**同じカメラで実写と仮想を重ねて見比べられる**
 * （回すと 90° 食い違って比べられない）。
 *
 * ## 平面の大きさ ＝ 盤そのもの
 *
 * 合成は `hand2 / field2 / field1 / hand1` の縦積みで、マット（field）は 1 枚 630px。
 * 平面は**マット 2 枚ぶん**（動画の幅 × 1260）で、動画を中央に置くと**両端の手札の帯は平面の外**に出る
 * （`drawField` が平面で切る）。上下の手札の高さが揃っていることが前提で、これは
 * `utils/defaultOverlayZones.js::configGuessIssue` が先に弾く。
 */
import { configGuessIssue, defaultOverlayZones } from '../../utils/defaultOverlayZones';
import { centerToPlane, planeToCenter, toField } from '../core/field';

/** マット（field レイヤー）1 枚の高さ（同梱プレイマット。`utils/defaultOverlayZones.json` の `field_size.height`） */
export const MAT_HEIGHT = 630;

/** 実写の平面（**マット 2 枚ぶん**。手札の帯は入らない） */
export const LIVE_PLANE = Object.freeze({ w: 1080, h: MAT_HEIGHT * 2 });

/** 動画の寸法から平面の大きさを決める（幅は動画のまま・高さはマット 2 枚ぶん） */
export const planeForVideo = (videoSize) => (
  videoSize?.w > 0 ? Object.freeze({ w: videoSize.w, h: LIVE_PLANE.h }) : LIVE_PLANE
);

/**
 * 動画のピクセル → 平面の座標に写す関数を作る（**中心を合わせて置くだけ**）。
 * 計算は `core/field::centerToPlane`（テーマも同じ計算を使うため core にある）。
 * @param {{videoSize: {w: number, h: number}|null|undefined, plane?: {w: number, h: number}, scale?: number}} opts
 */
export const videoToPlane = ({ videoSize, plane = LIVE_PLANE, scale = 1 }) => (
  centerToPlane({ size: videoSize, plane, scale })
);

/** 平面の座標 → 動画のピクセル（`videoToPlane` の逆） */
export const planeToVideo = ({ videoSize, plane = LIVE_PLANE, scale = 1 }) => (
  planeToCenter({ size: videoSize, plane, scale })
);

/** 動画のピクセルの矩形 → 4 点（左上・右上・右下・左下） */
export const rectCorners = ({ x, y, w, h }) => [[x, y], [x + w, y], [x + w, y + h], [x, y + h]];

/**
 * 実写の Field。
 * @param {object} opts
 * @param {Record<string, {x: number, y: number, w: number, h: number}>|null} opts.zones `overlay_zones` の矩形（動画のピクセル）
 * @param {{w: number, h: number}} opts.videoSize 動画の実寸
 * @param {{w: number, h: number}} [opts.plane]
 * @param {number} [opts.scale]
 */
export function liveField({ zones, videoSize, plane = LIVE_PLANE, scale = 1 }) {
  const toPlane = videoToPlane({ videoSize, plane, scale });
  const quads = {};
  Object.entries(zones ?? {}).forEach(([key, rect]) => {
    if (!rect || !(rect.w > 0) || !(rect.h > 0)) return;
    quads[key] = rectCorners(rect).map(toPlane);
  });
  return toField({ quads, plane, virtual: false });
}

/* ------------------------------------------------------------------ *
 *  ゾーンの矩形を引く（動画のピクセル）
 * ------------------------------------------------------------------ */

/**
 * **実写のゾーンの矩形**（動画のピクセル）と、その出所。
 *
 * 1. フィールド生成の config に `overlay_zones` があればそれ（config-points で 4 点を合わせ直したときに焼き込まれる）
 * 2. 無ければ**同梱プレイマットからの推定**（動画の寸法から合成の構成を復元する。`utils/defaultOverlayZones.js`）
 * 3. どちらも駄目なら理由を返す（画面にそのまま出す）
 *
 * `duelEditor` で `utils/` を読むのはここだけ（api も React も知らない純関数）。
 *
 * @param {object|null} fieldGenConfig `GetFieldGenConfig` の中身
 * @param {{w: number, h: number}|null} videoSize 今見ている動画の実寸
 * @returns {{zones: object|null, source: 'config'|'guess'|null, reason: string|null}}
 */
export function resolveLiveZones(fieldGenConfig, videoSize) {
  // 寸法が来る前（`loadedmetadata` の前）は 0。0 のまま先へ進めて「判別できません」に落とす
  const w = videoSize?.w ?? 0; const h = videoSize?.h ?? 0;
  const saved = fieldGenConfig?.overlay_zones;
  if (saved?.zones && saved.video_width > 0 && saved.video_height > 0) {
    // 低画質（動画1）と高画質（動画2）は同じ寸法だが、違う寸法で見ているなら比で直す
    const sx = w > 0 ? w / saved.video_width : 1;
    const sy = h > 0 ? h / saved.video_height : 1;
    const zones = Object.fromEntries(Object.entries(saved.zones)
      .map(([k, r]) => [k, { x: r.x * sx, y: r.y * sy, w: r.w * sx, h: r.h * sy }]));
    return { zones, source: 'config', reason: null };
  }
  const issue = configGuessIssue(fieldGenConfig);
  if (issue) return { zones: null, source: null, reason: issue };
  const guessed = defaultOverlayZones(w, h);
  if (!guessed) return { zones: null, source: null, reason: '動画の寸法から合成の構成を判別できません' };
  return { zones: guessed.zones, source: 'guess', reason: '同梱プレイマットからの推定です' };
}
